import { useTheme } from '@mui/material';
import { PieChart as MuiPieChart } from '@mui/x-charts';

import {
  CohortCharacteristicsResultType,
  CohortSummaryAttributeResult,
} from '@verantos-dev/types-cohorts';

import { wrapLabel } from '../helpers';

/**
 * Fixed chart height. The donut card ({@link ChartLayout} → DataGridLayout)
 * resolves a concrete 350px box, so a fixed height keeps the chart from
 * collapsing while width stays responsive.
 */
const CHART_HEIGHT = 280;
// Percentage radii (relative to the available drawing radius) instead of
// fixed pixels: the donut keeps a proportional hole at any size, so it
// stays a recognisable donut when the card narrows — a fixed px inner
// radius would approach the outer radius and collapse into a thin ring.
const INNER_RADIUS = '62%';
const OUTER_RADIUS = '100%';

type PieChartProps = {
  data: CohortSummaryAttributeResult[] | undefined;
  resultType: CohortCharacteristicsResultType;
};

export const PieChart = ({ data, resultType }: PieChartProps) => {
  const theme = useTheme();

  if (!data) return null;

  const donutSliceColor = [
    theme.palette.tertiary.lightBlue,
    theme.palette.tertiary.darkBlue,
    theme.palette.tertiary.green,
    theme.palette.tertiary.yellow,
    theme.palette.tertiary.orange,
  ];

  const pieChartData = data.map((item, index) => {
    const formattedLabel = wrapLabel(item.attributeName || '', 18);

    return {
      id: index,
      value: item.attributeCount,
      label: formattedLabel,
      percentage: item.attributePercentage,
      color: donutSliceColor[index % donutSliceColor.length],
    };
  });

  return (
    // Width omitted → MUI X Charts fills the card width via its internal
    // ResizeObserver, so the donut reflows when the column is squeezed
    // (e.g. the Research Assistant sidebar opening beside the Results
    // tab). Height is fixed so the card always resolves a concrete box.
    // The right margin must be at least as wide as the legend column
    // (marker + wrapped label) — otherwise the legend overflows left and
    // overlaps the donut. The donut lives in the remaining drawing area,
    // the legend in this reserved right strip; the two never collide.
    <MuiPieChart
      data-testid={`cohortCharacteristic-graph-pie-chart-${resultType}`}
      margin={{ left: 24, right: 150, top: 24, bottom: 24 }}
      height={CHART_HEIGHT}
      series={[
        {
          data: pieChartData || [],
          type: 'pie',
          innerRadius: INNER_RADIUS,
          outerRadius: OUTER_RADIUS,
          valueFormatter: (item, context) => {
            return `${pieChartData[context.dataIndex].value.toLocaleString()} (${
              pieChartData[context.dataIndex].percentage || 0
            }%)`;
          },
        },
      ]}
      slotProps={{
        legend: {
          direction: 'column',
          position: { horizontal: 'right', vertical: 'middle' },
          padding: 0,
          markGap: 5,
          itemGap: 16,
          labelStyle: {
            fontSize: 14,
            fontWeight: 400,
            width: 100,
            whiteSpace: 'normal',
          },
        },
        popper: {
          sx: {
            '& .MuiChartsTooltip-labelCell': {
              backgroundColor: `${theme.palette.white.main} !important`,
              '& .MuiTypography-root': {
                color: `${theme.palette.text.secondary}  !important`,
              },
            },
            '& .MuiChartsTooltip-valueCell': {
              backgroundColor: `${theme.palette.white.main} !important`,
              paddingLeft: '5px !important',
              '& .MuiTypography-root': {
                color: `${theme.palette.text.primary}  !important`,
              },
            },
          },
        },
      }}
    ></MuiPieChart>
  );
};
